import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Grid, Button, TextField, MenuItem, Stack, Chip, Divider,
  Table, TableHead, TableRow, TableCell, TableBody, IconButton, CircularProgress,
  Alert, Snackbar, Tooltip, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import AddIcon from '@mui/icons-material/Add';
import LockIcon from '@mui/icons-material/Lock';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaymentsIcon from '@mui/icons-material/Payments';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import EditIcon from '@mui/icons-material/Edit';
import { collection, query, where, onSnapshot, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { useBranch } from '../../context/BranchContext';
import { getTurnoActivo, getCajas, abrirTurno, crearCaja } from '../../services/cajaService';
import { generateBoletaPDF } from '../../services/boletaPDF';
import { CLP, FINANCE_COLORS, fmtFechaHora, labelMedio } from '../../utils/finance';
import CierreCaja from '../../components/caja/CierreCaja';
import EditVentaDialog from '../../components/caja/EditVentaDialog';

const { PRIMARY, TEAL, AMBER, SUCCESS } = FINANCE_COLORS;

export default function CajaDashboard() {
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  const { activeBranchId, activeBranch } = useBranch();
  const canManage = ['admin', 'gestion'].includes(userData?.role);

  const [loading, setLoading] = useState(true);
  const [turno, setTurno] = useState(null);
  const [cajas, setCajas] = useState([]);
  const [branches, setBranches] = useState([]);
  const [ventas, setVentas] = useState([]);
  const [cierreOpen, setCierreOpen] = useState(false);
  const [ventaEdit, setVentaEdit] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Apertura
  const [selCaja, setSelCaja] = useState('');
  const [montoApertura, setMontoApertura] = useState('');
  const [abriendo, setAbriendo] = useState(false);
  const [nuevaCajaOpen, setNuevaCajaOpen] = useState(false);
  const [nuevaCajaNombre, setNuevaCajaNombre] = useState('');
  const [nuevaCajaBranch, setNuevaCajaBranch] = useState(activeBranchId || '');

  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [t, cjs, brSnap] = await Promise.all([
        getTurnoActivo({ branchId: activeBranchId }),
        getCajas(activeBranchId),
        getDocs(collection(db, 'branches')),
      ]);
      setTurno(t);
      setCajas(cjs);
      setBranches(brSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      if (cjs.length === 1) setSelCaja(cjs[0].id);
    } catch (e) {
      notify('Error al cargar caja: ' + e.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [activeBranchId]);

  useEffect(() => { cargar(); }, [cargar]);

  // Ventas del turno en tiempo real
  useEffect(() => {
    if (!turno?.id) { setVentas([]); return; }
    const unsub = onSnapshot(
      query(collection(db, 'Sales'), where('turnoId', '==', turno.id)),
      (snap) => setVentas(
        snap.docs.map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
      ),
    );
    return () => unsub();
  }, [turno?.id]);

  const stats = useMemo(() => {
    const s = { total: 0, count: 0, efectivo: 0, tarjetas: 0, transferencia: 0 };
    ventas.filter((v) => v.estado !== 'anulada').forEach((v) => {
      s.total += v.totalFinal || v.total || 0; s.count += 1;
      (v.pagos || []).forEach((p) => {
        if (p.medio === 'efectivo') s.efectivo += p.monto || 0;
        else if (p.medio === 'debito' || p.medio === 'credito') s.tarjetas += p.monto || 0;
        else if (p.medio === 'transferencia') s.transferencia += p.monto || 0;
      });
    });
    return s;
  }, [ventas]);

  const handleAbrir = async () => {
    const caja = cajas.find((c) => c.id === selCaja);
    if (!caja) { notify('Selecciona una caja', 'warning'); return; }
    setAbriendo(true);
    try {
      const t = await abrirTurno({
        caja, montoApertura,
        usuario: { uid: currentUser?.uid, name: userData?.name, email: currentUser?.email },
      });
      setTurno(t);
      notify('Turno abierto correctamente');
    } catch (e) {
      notify('Error al abrir turno: ' + e.message, 'error');
    } finally {
      setAbriendo(false);
    }
  };

  const handleCrearCaja = async () => {
    if (!nuevaCajaNombre.trim()) { notify('Ingresa un nombre', 'warning'); return; }
    try {
      const br = branches.find((b) => b.id === nuevaCajaBranch);
      const id = await crearCaja({ nombre: nuevaCajaNombre.trim(), branchId: nuevaCajaBranch, branchName: br?.name || '' });
      setNuevaCajaOpen(false); setNuevaCajaNombre(''); setNuevaCajaBranch('');
      await cargar();
      setSelCaja(id);
      notify('Caja creada');
    } catch (e) {
      notify('Error al crear caja: ' + e.message, 'error');
    }
  };

  const reimprimir = async (v) => {
    await generateBoletaPDF(
      v, { name: v.ownerName, rut: v.ownerRut }, { name: v.patientName },
      { branch: activeBranch },
    );
  };

  if (loading) {
    return <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;
  }

  // ── Sin turno: pantalla de apertura ──────────────────────────────────────
  if (!turno) {
    return (
      <Box sx={{ maxWidth: 520, mx: 'auto', mt: 6 }}>
        <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 3, p: 4 }}>
          <Stack alignItems="center" spacing={1} sx={{ mb: 3 }}>
            <PointOfSaleIcon sx={{ fontSize: 48, color: PRIMARY }} />
            <Typography variant="h5" fontWeight={800} color={PRIMARY}>Apertura de Caja</Typography>
            <Typography variant="body2" color="text.secondary">No hay un turno abierto hoy. Abre la caja para comenzar a cobrar.</Typography>
          </Stack>

          {cajas.length === 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              No hay cajas configuradas.{canManage ? ' Crea una para empezar.' : ' Solicita a un administrador que cree una.'}
            </Alert>
          ) : (
            <TextField select fullWidth label="Caja" value={selCaja} onChange={(e) => setSelCaja(e.target.value)}
              InputLabelProps={{ shrink: true }} sx={{ mb: 2 }}>
              {cajas.map((c) => <MenuItem key={c.id} value={c.id}>{c.nombre}{c.branchName ? ` — ${c.branchName}` : ''}</MenuItem>)}
            </TextField>
          )}

          {canManage && (
            <Button size="small" startIcon={<AddIcon />} onClick={() => setNuevaCajaOpen(true)} sx={{ mb: 2 }}>
              Nueva caja
            </Button>
          )}

          <TextField fullWidth type="number" label="Monto fondo inicial" value={montoApertura}
            onChange={(e) => setMontoApertura(e.target.value)} InputLabelProps={{ shrink: true }}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} sx={{ mb: 3 }} />

          <Button fullWidth variant="contained" size="large" disabled={!selCaja || abriendo}
            startIcon={abriendo ? <CircularProgress size={16} /> : <PointOfSaleIcon />}
            onClick={handleAbrir} sx={{ bgcolor: PRIMARY, fontWeight: 700 }}>
            {abriendo ? 'Abriendo…' : 'Abrir turno'}
          </Button>
        </Paper>

        <Dialog open={nuevaCajaOpen} onClose={() => setNuevaCajaOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Nueva caja</DialogTitle>
          <DialogContent>
            <TextField fullWidth label="Nombre" value={nuevaCajaNombre} onChange={(e) => setNuevaCajaNombre(e.target.value)}
              InputLabelProps={{ shrink: true }} sx={{ mt: 1, mb: 2 }} placeholder="Caja Recepción" />
            <TextField select fullWidth label="Sucursal" value={nuevaCajaBranch} onChange={(e) => setNuevaCajaBranch(e.target.value)}
              InputLabelProps={{ shrink: true }}>
              <MenuItem value="">Sin sucursal</MenuItem>
              {branches.map((b) => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
            </TextField>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setNuevaCajaOpen(false)}>Cancelar</Button>
            <Button variant="contained" onClick={handleCrearCaja}>Crear</Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
        </Snackbar>
      </Box>
    );
  }

  // ── Turno abierto: dashboard ──────────────────────────────────────────────
  return (
    <Box sx={{ pb: 6 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" fontWeight={800} color={PRIMARY}>Caja</Typography>
          <Typography variant="body2" color="text.secondary">
            {turno.cajaNombre} · Abierta {fmtFechaHora(turno.aperturaAt)} · Cajero {turno.usuarioAperturaNombre}
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<MenuBookIcon />} onClick={() => navigate('/caja/libro-ventas')}>Libro de Ventas</Button>
        <Button variant="outlined" color="error" startIcon={<LockIcon />} onClick={() => setCierreOpen(true)}>Cerrar Turno</Button>
        <Button variant="contained" size="large" startIcon={<AddIcon />} onClick={() => navigate('/caja/ticket')}
          sx={{ bgcolor: SUCCESS, fontWeight: 700, '&:hover': { bgcolor: '#0e9e6e' } }}>Nueva Venta</Button>
      </Stack>

      {/* Stat cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <StatCard icon={<ReceiptIcon />} label="Ventas del turno" value={CLP(stats.total)} color={PRIMARY} />
        <StatCard icon={<PointOfSaleIcon />} label="Transacciones" value={stats.count} color={TEAL} />
        <StatCard icon={<PaymentsIcon />} label="Efectivo" value={CLP(stats.efectivo)} color={SUCCESS} />
        <StatCard icon={<CreditCardIcon />} label="Tarjetas" value={CLP(stats.tarjetas)} color={AMBER} />
        <StatCard icon={<SwapHorizIcon />} label="Transferencias" value={CLP(stats.transferencia)} color="#6366F1" />
      </Grid>

      {/* Lista de ventas */}
      <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 2 }}>
        <Box sx={{ p: 2, borderBottom: '1px solid #E2E8F0' }}>
          <Typography variant="subtitle1" fontWeight={700}>Ventas del turno</Typography>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700 } }}>
              <TableCell>Hora</TableCell>
              <TableCell>Propietario</TableCell>
              <TableCell>N° Boleta</TableCell>
              <TableCell>Medio</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ventas.length === 0 && (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                Aún no hay ventas en este turno.
              </TableCell></TableRow>
            )}
            {ventas.map((v) => (
              <TableRow key={v.id} hover sx={{ opacity: v.estado === 'anulada' ? 0.5 : 1 }}>
                <TableCell>{fmtFechaHora(v.createdAt).split(' ')[1] || '–'}</TableCell>
                <TableCell>{v.ownerName || v.patientName || '–'}</TableCell>
                <TableCell>{v.folioDocumento || v.boletaNumber || '–'}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5}>
                    {(v.pagos || [{ medio: v.paymentMethod }]).map((p, i) => (
                      <Chip key={i} label={labelMedio(p.medio)} size="small" variant="outlined" />
                    ))}
                  </Stack>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{CLP(v.totalFinal || v.total)}</TableCell>
                <TableCell align="center">
                  <Tooltip title="Reimprimir boleta">
                    <IconButton size="small" onClick={() => reimprimir(v)}><ReceiptIcon fontSize="small" /></IconButton>
                  </Tooltip>
                  {canManage && v.estado !== 'anulada' && (
                    <Tooltip title="Editar monto y medio de pago">
                      <IconButton size="small" onClick={() => setVentaEdit(v)}><EditIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <CierreCaja open={cierreOpen} onClose={() => setCierreOpen(false)} turno={turno}
        onClosed={() => { setCierreOpen(false); setTurno(null); notify('Turno cerrado'); cargar(); }} />

      <EditVentaDialog open={!!ventaEdit} onClose={() => setVentaEdit(null)} venta={ventaEdit} turno={turno}
        onSaved={() => { setVentaEdit(null); notify('Venta actualizada'); cargar(); }} />

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

function StatCard({ icon, label, value, color }) {
  return (
    <Grid size={{ xs: 6, md: 2.4 }}>
      <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 2, p: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
          <Box sx={{ color }}>{icon}</Box>
          <Typography variant="caption" color="text.secondary" noWrap>{label}</Typography>
        </Stack>
        <Typography variant="h6" fontWeight={800} sx={{ color }}>{value}</Typography>
      </Paper>
    </Grid>
  );
}
